import React, { useState, useEffect } from "react";
import { X, Plus, FlaskRound as Flask, Loader, Search } from "lucide-react";
import { supabase } from "../lib/supabase";

interface LabExerciseSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (exerciseId: string) => void;
  selectedExerciseId: string | null;
}

interface LabExercise {
  id: string;
  title: string;
  description: string;
  order_number: number;
  is_active: boolean;
}

export default function LabExerciseSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedExerciseId,
}: LabExerciseSelectorModalProps) {
  const [exercises, setExercises] = useState<LabExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchExercises();
    }
  }, [isOpen]);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("lab_exercises")
        .select("*")
        .order("order_number");

      if (error) throw error;
      setExercises(data || []);
    } catch (err) {
      console.error("Error fetching lab exercises:", err);
      setError("Failed to load lab exercises");
    } finally {
      setLoading(false);
    }
  };

  const filteredExercises = exercises.filter(
    (exercise) =>
      exercise.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exercise.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Select Lab Exercise
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search lab exercises..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : filteredExercises.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No lab exercises found</p>
              <p className="text-sm text-gray-400 mt-2">
                Create lab exercises in the admin panel
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredExercises.map((exercise) => (
                <button
                  key={exercise.id}
                  onClick={() => onSelect(exercise.id)}
                  className={`w-full text-left p-4 rounded-lg border ${
                    selectedExerciseId === exercise.id
                      ? "border-[#F98B3D] bg-orange-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  } transition-colors duration-200`}
                >
                  <div className="flex items-start">
                    <Flask
                      className={`h-5 w-5 mt-1 mr-3 ${
                        selectedExerciseId === exercise.id
                          ? "text-[#F98B3D]"
                          : "text-gray-400"
                      }`}
                    />
                    <div>
                      <h3
                        className={`font-medium ${
                          selectedExerciseId === exercise.id
                            ? "text-[#F98B3D]"
                            : "text-gray-900"
                        }`}
                      >
                        {exercise.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {exercise.description}
                      </p>
                      {!exercise.is_active && (
                        <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-between">
          <button
            onClick={() => window.open("/admin/lab-exercises/new", "_blank")}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
